/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { FC, useEffect, useRef } from 'react'
import { Field, Form, Formik, FormikProps } from 'formik'
import * as Yup from 'yup'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Form as NXUIForm, Lightbox } from '@nexigendigital/nxui'
import { useSavePaymentMethodMutation } from 'api/billing'

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
import styled from 'styled-components'

const Payment = {
    Form: styled(Form)`
        label {
            width: 100% !important;
        }

        > div:last-of-type {
            margin-bottom: 0;
        }
    `
}

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
type AddPaymentMethodLightboxProps = {
    status: boolean,
    close: () => void
}

export const AddPaymentMethodLightbox: FC<AddPaymentMethodLightboxProps> = ({ status, close }) => {
    /*   STATE
    **********************************************************************************************************/
    const formRef = useRef<FormikProps<{
        card_holder: string;
        card_number: string;
        expiry: string;
        is_default: boolean;
    }>>(null)

    /*   QUERIES & MUTATIONS
    **********************************************************************************************************/
    const [savePaymentMethod, { isSuccess: savePaymentMethodSuccess, isError: savePaymentMethodError }] = useSavePaymentMethodMutation()

    /*   EFFECTS
    **********************************************************************************************************/
    useEffect(() => {
        if (savePaymentMethodSuccess) {
            close()
        }

        if (savePaymentMethodError && formRef.current) {
            formRef.current.setSubmitting(false)
        }
    }, [savePaymentMethodSuccess, savePaymentMethodError])

    /*   RENDER COMPONENT
    **********************************************************************************************************/
    return (
        <Lightbox
            title={'Add New Payment Method'}
            conditions={{ state: status, action: () => close() }}
            close={() => close()}
            content={
                <Formik
                    innerRef={formRef}
                    initialValues={{
                        card_holder: '',
                        card_number: '',
                        expiry: '',
                        is_default: false
                    }}
                    validationSchema={
                        Yup.object({
                            card_holder: Yup.string().required('Required'),
                            card_number: Yup.string().required('Card Number is required'),
                            expiry: Yup.string().required('Required'),
                            is_default: Yup.boolean()
                        })
                    }
                    onSubmit={({ card_number, expiry, ...values }) => {
                        const formattedExpiry = expiry.split('/')
                        savePaymentMethod({
                            ...values,
                            card_number: card_number.replace(/ /g, ''),
                            expiry: `${formattedExpiry[0]}/${formattedExpiry[1]}`.replace(/ /g, '')
                        })
                    }}
                >
                    {({ isSubmitting }) => (
                        <Payment.Form>
                            <Field
                                id={'card_holder'}
                                label={'Cardholder Name'}
                                name={'card_holder'}
                                type={'text'}
                                disabled={isSubmitting}
                                autoComplete={'name'}
                                component={NXUIForm.InputField}
                            />
                            <Field
                                id={'card_details'}
                                label={'Card Details'}
                                disabled={isSubmitting}
                                component={NXUIForm.PaymentField}
                            />
                            <Field
                                id={'is_default'}
                                label={'Set as primary'}
                                name={'is_default'}
                                type={'checkbox'}
                                component={NXUIForm.CheckboxField}
                            />
                        </Payment.Form>
                    )}
                </Formik>
            }
            actions={[
                {
                    label: 'Back',
                    color: 'secondary',
                    func: () => close()
                },
                {
                    label: 'Add',
                    color: 'primary',
                    loading: formRef?.current?.isSubmitting,
                    disabled: formRef?.current?.isSubmitting,
                    func: () => formRef?.current?.submitForm()
                }
            ]}
        />
    )
}
